/**
 * Collections Page Component
 * ===========================
 *
 * Replaces the Gallery component with a vertical stack of carousels.
 * Each collection gets its own carousel section that can be scrolled through vertically.
 *
 * Features:
 * - Vertical scroll container with snap points
 * - One CustomCarousel per collection
 * - Uses images from projects data loaded by App.js
 * - Orders chronologically (newest first)
 * - Starts at specified projectId
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CustomCarousel from '../ui/CustomCarousel';
import Loading from '../ui/Loading';
import MetaTags, {
  defaultMetaData,
  getAboutMetaData,
  getCollectionPageStructuredData,
} from '../ui/MetaTags';
import TimelinePage from './TimelinePage';
import { fetchGarments } from '../../utils/garmentsAPI';
import { images, preloadImage } from '../../utils/assetUtils';
import './CollectionsPage.scss';
import '../pages/SplashPage.scss';
import Header from '../layout/Header';
import { getHeaderProject } from '../../utils/headerProject';
import { getViewportDebugSnapshot, isDebugFlagEnabled, roundDebugValue } from '../../utils/motionDebug';
import { debugLog, debugWarn, isDebugLoggingEnabled } from '../../utils/debug';

const CollectionsPage = ({ projects = [], onProjectSelect = () => { } }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize to -1 (splash section) for home route, will be updated by useEffect based on pathname
  const [currentSectionIndex, setCurrentSectionIndex] = useState(() => {
    // Default to splash (-1) for home route
    if (location.pathname === '/' && !new URLSearchParams(location.search).has('event')) {
      return -1;
    }
    return 0; // Default to 0 for other routes, will be updated by useEffect
  });
  const useIntersectionObserver = false; // switch off IO-based detection in favor of scroll-settle detection
  const scrollContainerRef = useRef(null);
  const lastProjectIdRef = useRef(null);
  const splashScrollContainerRef = useRef(null);
  const splashWindowContainerRef = useRef(null);
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  const [allowObserver, setAllowObserver] = useState(false); // Disable observer during initial scroll
  const [hasUserScrolled, setHasUserScrolled] = useState(false); // Track if user has manually scrolled
  const [isSplashWide, setIsSplashWide] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 0;
    const h = typeof window !== 'undefined' ? window.innerHeight : 0;
    return w > h || w > 940;
  });
  const currentSectionIndexRef = useRef(currentSectionIndex);
  const lastCommittedUrlRef = useRef(null);
  const debugMotionEnabled = useMemo(
    () => isDebugFlagEnabled('debugMotion', 'debugSlides'),
    []
  );
  const [garments, setGarments] = useState([]);
  const isMobileViewport = typeof window !== 'undefined'
    ? window.innerWidth < 768
    : false;

  useEffect(() => {
    currentSectionIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  // Fetch garments for flip-card enrichment
  useEffect(() => {
    fetchGarments().then(setGarments).catch(console.error);
  }, []);

  // Get about data for TimelinePage
  const aboutData = projects.find(p => p.id === 'about');

  // Preload splash high-res image and react to viewport for wide vs portrait
  useEffect(() => {
    const checkWide = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsSplashWide(w > h || w > 940);
    };
    checkWide();
    window.addEventListener('resize', checkWide);
    return () => window.removeEventListener('resize', checkWide);
  }, []);

  useEffect(() => {
    const url = isSplashWide ? images.splash.wide : images.splash.full;
    setIsHighResLoaded(false);
    preloadImage(url)
      .then(() => setIsHighResLoaded(true))
      .catch(console.error);
  }, [isSplashWide]);

  // Enrich collection images with garment data for flip cards
  const enrichProductsWithGarmentData = useCallback((collection, garmentsList) => {
    const garmentsBySlug = new Map(garmentsList.map(g => [g.slug, g]));
    const collectionGarments = garmentsList.filter((garment) => garment.collection === collection.airtableId);
    const normalizeText = (value = '') => value
      .toLowerCase()
      .replace(/[#']/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const inferGarmentSlugs = (img) => {
      const label = normalizeText(img.highlight?.text || img.navLabel || img.name || img.id);
      if (!label) return [];

      const directMatches = collectionGarments.filter((garment) => {
        const garmentName = normalizeText(garment.name);
        const garmentType = normalizeText(garment.type);
        const slugLabel = normalizeText(garment.slug?.replace(/-/g, ' '));
        const exactToken = garmentType ? `${garmentName} ${garmentType}` : garmentName;

        return (
          (exactToken && label.includes(exactToken)) ||
          (slugLabel && label.includes(slugLabel))
        );
      });

      if (directMatches.length > 0) {
        return [...new Set(directMatches.map((garment) => garment.slug))];
      }

      const partialMatches = collectionGarments.filter((garment) => {
        const garmentName = normalizeText(garment.name);
        return garmentName && label.includes(garmentName);
      });

      return [...new Set(partialMatches.map((garment) => garment.slug))];
    };

    const sortedImages = [...collection.images].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    return sortedImages.map(img => {
      const product = {
        id: img.id,
        order: img.order,
        name: img.highlight?.text || img.navLabel || img.id,
        type: '',
        images: [img.src]
      };
      const garmentSlugs = img.garmentSlugs && Array.isArray(img.garmentSlugs) && img.garmentSlugs.length > 0
        ? img.garmentSlugs
        : inferGarmentSlugs(img);

      if (garmentSlugs.length > 0) {
        const garmentData = garmentSlugs
          .map(slug => garmentsBySlug.get(slug))
          .filter(Boolean)
          .map(g => ({ name: g.name, type: g.type, materials: g.materials, price: g.price, season: g.season }));
        product.garmentData = garmentData;
        product.garmentSlugs = garmentSlugs;
      }
      return product;
    });
  }, []);

  // Filter and prepare collection data from projects
  const collections = useMemo(() => {
    if (!projects || projects.length === 0) return [];

    // Get all public projects with images (these are the collections)
    const collectionsWithImages = projects
      .filter(p =>
        p.status === 'Public' &&
        p.airtableId &&
        p.id !== 'about' &&
        p.images &&
        p.images.length > 0
      )
      .map(project => {
        // Filter out the 'about' intro image, keep only the actual collection images
        const collectionImages = project.images
          .filter(img => img.id !== 'about')
          .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

        if (collectionImages.length === 0) {
          return null;
        }

        const aboutImage = project.images.find(img => img.id === 'about');
        return {
          projectId: project.id,
          projectName: project.name,
          airtableId: project.airtableId, // Add airtableId for garment matching
          season: project.season,
          themeColor: project.themeColor,
          order: project.order || 0,
          images: collectionImages,
          introText: aboutImage?.text || null,
          aboutImageSrc: aboutImage?.src || null,
          credits: project.credits || null
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => (b.order || 0) - (a.order || 0)); // Sort newest first

    return collectionsWithImages;
  }, [projects]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    lastCommittedUrlRef.current = `${window.location.pathname}${window.location.search}`;
  }, [location.pathname, location.search]);

  const getPathForSectionIndex = useCallback((sectionIndex) => {
    const params = new URLSearchParams(window.location.search);

    if (sectionIndex === -1) {
      return params.has('event') ? null : '/';
    }

    if (sectionIndex >= 0 && sectionIndex < collections.length) {
      return `/${collections[sectionIndex].projectId}`;
    }

    if (sectionIndex === collections.length) {
      return params.has('event') ? null : '/about';
    }

    return null;
  }, [collections]);

  const commitSectionUrl = useCallback((sectionIndex, source) => {
    if (typeof window === 'undefined') return;

    const nextPath = getPathForSectionIndex(sectionIndex);
    if (!nextPath) return;

    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (currentPath === nextPath || lastCommittedUrlRef.current === nextPath) {
      return;
    }

    lastCommittedUrlRef.current = nextPath;
    debugLog(`[CollectionsPage][${source}] URL -> ${nextPath}`);
    window.history.replaceState(null, '', nextPath);
  }, [getPathForSectionIndex]);

  // Update scroll states - just mark active section (no visual transforms)
  const updateScrollStates = useCallback((activeIndex) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    if (process.env.NODE_ENV === 'development') {
      debugLog('[CollectionsPage][updateScrollStates] activeIndex=', activeIndex);
    }

    const sections = scrollContainer.querySelectorAll('.splash-section, .collection-section, .about-section');
    sections.forEach((section) => {
      const sectionIndex = parseInt(section.dataset.sectionIndex, 10);

      // Only mark active section - no visual transforms needed
      if (sectionIndex === activeIndex) {
        section.setAttribute('data-scroll-state', 'active');
      } else {
        section.removeAttribute('data-scroll-state');
      }
    });
  }, []);

  // Scroll to a specific section (like Gallery.js - simple native scroll)
  const scrollToSection = useCallback((index, smooth = false) => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const sections = scrollContainer.querySelectorAll('.splash-section, .collection-section, .about-section');
      // Adjust index for splash (-1 becomes 0, 0 becomes 1, etc.)
      const sectionIndex = index + 1;
      if (sections[sectionIndex]) {
        if (process.env.NODE_ENV === 'development') {
          debugLog('[CollectionsPage][scrollToSection] request', {
            requestedIndex: index,
            domSectionIndex: sectionIndex,
            sectionId: sections[sectionIndex].id,
            smooth
          });
        }
        // Use smooth for user interactions, auto for initial programmatic scrolls
        sections[sectionIndex].scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      }
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName;
        if (target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
          return;
        }
      }

      let nextSectionIndex = null;
      const maxSectionIndex = collections.length;
      const currentIndex = currentSectionIndexRef.current;

      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        nextSectionIndex = Math.min(currentIndex + 1, maxSectionIndex);
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        nextSectionIndex = Math.max(currentIndex - 1, -1);
      } else {
        return;
      }

      if (nextSectionIndex === currentIndex) {
        return;
      }

      event.preventDefault();
      setHasUserScrolled(true);
      scrollToSection(nextSectionIndex, true);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [collections.length, scrollToSection]);

  // Find initial section index based on projectId or pathname (like Gallery.js - simple)
  useEffect(() => {
    if (collections.length === 0) return;

    debugLog('[CollectionsPage] Initial scroll logic:', {
      pathname: location.pathname,
      projectId,
      search: location.search,
      collectionsLength: collections.length
    });

    let targetIndex = -1; // Default to splash

    // Check for event query parameter
    const params = new URLSearchParams(location.search);
    const hasEventQuery = params.has('event');

    if (location.pathname === '/' && !hasEventQuery) {
      // Home route without event - scroll to splash
      targetIndex = -1;
      debugLog('[CollectionsPage] Setting targetIndex to -1 (splash) for home route');
    } else if (projectId === 'about' || location.pathname === '/about' || location.pathname.startsWith('/about') || hasEventQuery) {
      // About route or event query - scroll to about section (last section)
      targetIndex = collections.length;
      debugLog('[CollectionsPage] Setting targetIndex to', targetIndex, '(about section)');
    } else if (location.pathname === '/' && hasEventQuery) {
      // Root with event query - scroll to about
      targetIndex = collections.length;
      debugLog('[CollectionsPage] Setting targetIndex to', targetIndex, '(about section) for root with event');
    } else {
      // Collection route - find matching collection
      const sectionIndex = collections.findIndex(s => s.projectId === projectId);
      if (sectionIndex !== -1) {
        targetIndex = sectionIndex;
        debugLog('[CollectionsPage] Setting targetIndex to', targetIndex, 'for collection:', projectId);
      } else {
        // ProjectId not found, default to splash
        targetIndex = -1;
        debugLog('[CollectionsPage] ProjectId not found, defaulting to -1 (splash)');
      }
    }

    debugLog('[CollectionsPage] Final targetIndex:', targetIndex);
    setCurrentSectionIndex(targetIndex);
    updateScrollStates(targetIndex);

    // Scroll to section - simple native scroll (CSS scroll-snap handles smooth snapping)
    // Wait for DOM to be ready, then scroll
    const scrollTimeout = setTimeout(() => {
      debugLog('[CollectionsPage] Scrolling to section:', targetIndex);
      scrollToSection(targetIndex);
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(scrollTimeout);
  }, [projectId, collections.length, location.pathname, location.search, scrollToSection]);

  // Track user scroll to enable URL updates (like Gallery.js)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      debugLog('[CollectionsPage] Scroll tracker: No container');
      return;
    }

    let handleScroll = null;
    let enableTimeout = null;

    debugLog('[CollectionsPage] Scroll tracker: Setting up (will enable after 200ms)');

    // Wait a bit before enabling scroll tracking to avoid programmatic scrolls
    enableTimeout = setTimeout(() => {
      debugLog('[CollectionsPage] Scroll tracker: Now listening for scroll events');
      handleScroll = () => {
        // Enable observer after first user scroll
        if (!hasUserScrolled) {
          debugLog('[CollectionsPage] Scroll tracker: User scrolled! Enabling URL updates');
          setHasUserScrolled(true);
          setAllowObserver(true);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }, 200); // Wait 200ms after mount before tracking scrolls (reduced from 1s)

    return () => {
      if (enableTimeout) {
        clearTimeout(enableTimeout);
      }
      if (handleScroll) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasUserScrolled]);

  // Handle programmatic scroll requests passed via navigation state
  useEffect(() => {
    if (!collections.length) return;
    if (!location.state) return;

    const { scrollToAbout, scrollToTop, ...restState } = location.state || {};
    if (!scrollToAbout && !scrollToTop) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const performScroll = () => {
      if (scrollToTop) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (scrollToAbout) {
        const aboutSection = scrollContainer.querySelector('.about-section');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(performScroll);
    });

    navigate(location.pathname, {
      replace: true,
      state: Object.keys(restState).length ? restState : undefined
    });

    return () => cancelAnimationFrame(rafId);
  }, [collections.length, location.pathname, location.state, navigate]);

  // IntersectionObserver to track which section is in view with minimal oscillation
  useEffect(() => {
    if (!useIntersectionObserver) return;
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || collections.length === 0) return;

    if (process.env.NODE_ENV === 'development') {
      debugLog('[CollectionsPage][IO] init', { threshold: [0.35, 0.6, 0.95], rootIsContainer: true, sections: collections.length, rootMargin: '0px 0px -10% 0px' });
    }

    // Track last committed index and a small cooldown to avoid oscillation churn
    const lastActiveIndexRef = { current: null };
    const lastCommitTimeRef = { current: 0 };
    const MIN_SWITCH_INTERVAL_MS = 450;

    // Simple observer like Gallery.js - just track which section is visible
    const observer = new IntersectionObserver(
      (entries) => {
        // Consider only intersecting entries and choose the one with the highest ratio
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length === 0) return;
        const best = visible.reduce((a, b) =>
          (a.intersectionRatio >= b.intersectionRatio ? a : b)
        );

        const index = parseInt(best.target.dataset.sectionIndex, 10);
        if (process.env.NODE_ENV === 'development') {
          debugLog('[CollectionsPage][IO] intersect (best)', {
            id: best.target.id,
            sectionIndex: index,
            ratio: best.intersectionRatio,
            lastActive: lastActiveIndexRef.current,
            hasUserScrolled
          });
        }

        // Soft hysteresis: if we switched very recently, ignore unless ratio is very high
        const now = Date.now();
        const timeSinceLast = now - lastCommitTimeRef.current;
        const isVeryStrongSignal = best.intersectionRatio >= 0.9;
        if (timeSinceLast < MIN_SWITCH_INTERVAL_MS && !isVeryStrongSignal) {
          if (process.env.NODE_ENV === 'development') {
            debugLog('[CollectionsPage][IO] debounce switch', { timeSinceLast, MIN_SWITCH_INTERVAL_MS });
          }
          return;
        }

        // Ignore if unchanged to prevent oscillation churn
        if (lastActiveIndexRef.current === index) return;
        lastActiveIndexRef.current = index;
        lastCommitTimeRef.current = now;

        setCurrentSectionIndex(index);
        updateScrollStates(index);

        // Only update URL if user has scrolled (prevents initial URL change)
        if (hasUserScrolled) {
          commitSectionUrl(index, 'IO');
        }
      },
      {
        threshold: [0.35, 0.6, 0.95],
        root: scrollContainer,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    const sections = scrollContainer.querySelectorAll('.collection-section, .about-section, .splash-section');
    if (process.env.NODE_ENV === 'development') {
      debugLog('[CollectionsPage][IO] observing', [...sections].map(s => ({ id: s.id, idx: s.dataset.sectionIndex })));
    }
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, [collections, commitSectionUrl, hasUserScrolled, updateScrollStates]);

  // Scroll-based active-section detection with eager updates (immediate visibility, 30ms URL debounce)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || collections.length === 0) return;

    let lastScrollTop = scrollContainer.scrollTop;
    let scrollDirection = null; // 'up' | 'down' | null
    let lastPreviewIndex = null; // Track immediate preview to avoid redundant updates

    const calculateNearestSection = () => {
      const sections = scrollContainer.querySelectorAll('.splash-section, .collection-section, .about-section');
      if (sections.length === 0) return null;

      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportCenter = containerRect.top + containerRect.height / 2;

      let bestIndex = null;
      let bestScore = Number.POSITIVE_INFINITY;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const distanceFromViewport = Math.abs(sectionCenter - viewportCenter);
        const idx = parseInt(section.dataset.sectionIndex, 10);

        // Direction bias: prefer sections in the scroll direction
        let directionBonus = 0;
        if (scrollDirection === 'down' && sectionCenter > viewportCenter) {
          // Scrolling down: prefer sections below viewport (reduce distance score)
          directionBonus = -50; // Bias toward sections below
        } else if (scrollDirection === 'up' && sectionCenter < viewportCenter) {
          // Scrolling up: prefer sections above viewport
          directionBonus = -50; // Bias toward sections above
        }

        const score = distanceFromViewport + directionBonus;
        if (score < bestScore) {
          bestScore = score;
          bestIndex = idx;
        }
      });

      return bestIndex;
    };

    // Immediate preview update for navigation visibility (eager, no delay)
    const updatePreviewSection = () => {
      const bestIndex = calculateNearestSection();
      if (bestIndex === null || bestIndex === lastPreviewIndex) return;

      lastPreviewIndex = bestIndex;

      // Immediately update section index for visibility (affects TimelinePage navigation)
      setCurrentSectionIndex((prev) => {
        if (prev === bestIndex) return prev;
        if (process.env.NODE_ENV === 'development') {
          debugLog('[CollectionsPage][SCROLL] updatePreviewSection', {
            prevIndex: prev,
            newIndex: bestIndex,
            locationPathname: location.pathname,
            scrollTop: scrollContainer.scrollTop,
            scrollDirection
          });
        }
        updateScrollStates(bestIndex);
        return bestIndex;
      });
    };

    // Eager URL update with minimal debounce to avoid flicker during rapid scrolling
    const updateURL = () => {
      const bestIndex = calculateNearestSection();
      if (bestIndex === null || !hasUserScrolled) return;
      commitSectionUrl(bestIndex, 'SCROLL');
    };

    let urlUpdateTimer = null;
    const URL_UPDATE_DELAY = 180;

    const onScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;

      // Detect scroll direction
      if (currentScrollTop > lastScrollTop) {
        scrollDirection = 'down';
      } else if (currentScrollTop < lastScrollTop) {
        scrollDirection = 'up';
      }
      lastScrollTop = currentScrollTop;

      // Immediate preview update (affects navigation visibility - no delay)
      updatePreviewSection();

      // Commit URL only after scroll settles briefly to avoid Safari rate limits.
      if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
      urlUpdateTimer = setTimeout(updateURL, URL_UPDATE_DELAY);
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, [collections, commitSectionUrl, hasUserScrolled, location.pathname, updateScrollStates]);

  useEffect(() => {
    if (!debugMotionEnabled) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const getNearestSection = () => {
      const sections = scrollContainer.querySelectorAll('.splash-section, .collection-section, .about-section');
      if (!sections.length) return null;

      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportCenter = containerRect.top + containerRect.height / 2;
      let bestMatch = null;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = Math.abs(center - viewportCenter);

        if (!bestMatch || distance < bestMatch.distance) {
          bestMatch = {
            id: section.id,
            sectionIndex: Number(section.dataset.sectionIndex),
            distance: roundDebugValue(distance, 2)
          };
        }
      });

      return bestMatch;
    };

    const logMotion = (phase, extra = {}) => {
      debugLog('[CollectionsPage][motion]', {
        phase,
        currentSectionIndex: currentSectionIndexRef.current,
        scrollTop: roundDebugValue(scrollContainer.scrollTop, 2),
        clientHeight: roundDebugValue(scrollContainer.clientHeight, 2),
        scrollHeight: roundDebugValue(scrollContainer.scrollHeight, 2),
        nearestSection: getNearestSection(),
        viewport: getViewportDebugSnapshot(),
        ...extra
      });
    };

    let settleTimer = null;
    let lastScrollLogAt = 0;

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollLogAt >= 80) {
        lastScrollLogAt = now;
        logMotion('scroll');
      }

      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        logMotion('scroll-settle');
      }, 160);
    };

    const handleTouchStart = (event) => {
      const touch = event.touches?.[0];
      logMotion('touch-start', touch ? {
        touch: {
          x: roundDebugValue(touch.clientX, 2),
          y: roundDebugValue(touch.clientY, 2)
        }
      } : {});
    };

    const handleTouchEnd = (event) => {
      const touch = event.changedTouches?.[0];
      logMotion('touch-end', touch ? {
        touch: {
          x: roundDebugValue(touch.clientX, 2),
          y: roundDebugValue(touch.clientY, 2)
        }
      } : {});
    };

    const handleViewportResize = () => {
      logMotion('viewport-resize');
    };

    logMotion('debug-enabled', { sectionCount: collections.length + 2 });

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('resize', handleViewportResize);
    window.visualViewport?.addEventListener('resize', handleViewportResize);

    return () => {
      clearTimeout(settleTimer);
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleViewportResize);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [collections.length, debugMotionEnabled]);

  // Add body class for styling
  useEffect(() => {
    document.body.classList.add('collections-page-view');
    return () => {
      document.body.classList.remove('collections-page-view');
    };
  }, []);

  // Meta data for current section
  const currentSection = collections[currentSectionIndex];
  const hasEventQuery = useMemo(
    () => new URLSearchParams(location.search).has('event'),
    [location.search]
  );

  const metaData = useMemo(() => {
    if (!currentSection) return {};
    const firstImage = currentSection.images?.[0];
    const imageUrl = firstImage?.src
      ? `https://www.elodiecarstensen.com/assets/images/${firstImage.src}`
      : undefined;
    const description =
      currentSection.introText ||
      `Explore the ${currentSection.projectName} collection ${currentSection.season ? `(${currentSection.season})` : ''} by contemporary fashion artist Elodie Carstensen.`;
    return {
      title: `${currentSection.projectName} - Elodie Carstensen`,
      description,
      url: `https://www.elodiecarstensen.com/${currentSection.projectId}`,
      type: 'website',
      structuredData: getCollectionPageStructuredData(
        currentSection.projectName,
        currentSection.projectId,
        description,
        imageUrl
      ),
    };
  }, [currentSection]);

  // Determine theme color: greenish yellow for splash section, dark grey for everything else
  // Use URL as primary source of truth since it updates when scrolling and is what Safari sees
  const themeColor = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const hasEventQuery = params.has('event');

    // Use URL pathname as primary source - if URL is '/' (home), use greenish yellow
    // This ensures theme matches what's actually in the URL bar, which Safari uses
    if (location.pathname === '/' && !hasEventQuery) {
      // Also check currentSectionIndex as secondary check - if we've scrolled past splash, use dark grey
      // This handles the brief moment when URL hasn't updated yet but we've scrolled
      if (currentSectionIndex >= 0 && currentSectionIndex < collections.length) {
        // We've scrolled to a collection but URL hasn't updated yet - use dark grey
        if (process.env.NODE_ENV === 'development') {
          debugLog('[CollectionsPage][themeColor] ⬛ URL is / but scrolled to collection, using dark grey #091115', {
            currentSectionIndex,
            locationPathname: location.pathname,
            locationSearch: location.search
          });
        }
        return '#091115';
      }
      // On splash section with URL '/'
      if (process.env.NODE_ENV === 'development') {
        debugLog('[CollectionsPage][themeColor] ✅ URL is / (splash), using greenish yellow #514403', {
          currentSectionIndex,
          locationPathname: location.pathname,
          locationSearch: location.search
        });
      }
      return '#514403'; // Greenish yellow for splash/home
    }

    // Everything else (collections like /jnn-drc, /about, etc.) uses dark grey
    if (process.env.NODE_ENV === 'development') {
      debugLog('[CollectionsPage][themeColor] ⬛ URL is not /, using dark grey #091115', {
        currentSectionIndex,
        locationPathname: location.pathname,
        locationSearch: location.search,
        collectionsLength: collections.length,
        isAboutSection: currentSectionIndex === collections.length
      });
    }
    return '#091115'; // Dark grey for collections and other sections
  }, [currentSectionIndex, location.pathname, location.search, collections.length]);

  // Update theme color meta tag directly via DOM (more reliable than relying on Helmet alone)
  useEffect(() => {
    // Find ALL meta theme-color tags (might be multiple if Helmet created one)
    const metaThemeColorTags = document.querySelectorAll('meta[name="theme-color"]');

    if (metaThemeColorTags.length > 0) {
      // Update all meta tags to ensure consistency
      metaThemeColorTags.forEach((metaTag, index) => {
        metaTag.setAttribute('content', themeColor);

        if (process.env.NODE_ENV === 'development' && index === 0) {
          debugLog('[CollectionsPage][themeColor] ✅ Direct DOM update: meta theme-color ->', themeColor, `(found ${metaThemeColorTags.length} meta tag(s))`);
        }
      });

      // Force Safari to re-read the theme color by toggling visibility
      // This helps Safari pick up the change on iOS/macOS
      const firstMeta = metaThemeColorTags[0];
      const originalContent = firstMeta.getAttribute('content');

      // Force a reflow/repaint by briefly removing and re-adding
      if (firstMeta.parentNode) {
        const parent = firstMeta.parentNode;
        const nextSibling = firstMeta.nextSibling;
        parent.removeChild(firstMeta);
        // Force reflow
        void parent.offsetHeight;
        if (nextSibling) {
          parent.insertBefore(firstMeta, nextSibling);
        } else {
          parent.appendChild(firstMeta);
        }
        firstMeta.setAttribute('content', themeColor);
      }

      if (process.env.NODE_ENV === 'development') {
        // Verify it was actually updated
        setTimeout(() => {
          const updatedValue = metaThemeColorTags[0].getAttribute('content');
          if (updatedValue === themeColor) {
            debugLog('[CollectionsPage][themeColor] ✅ Verified: meta tag content is', updatedValue);
          } else {
            debugWarn('[CollectionsPage][themeColor] ⚠️ Update failed! Expected', themeColor, 'but got', updatedValue);
          }
        }, 10);
      }
    } else {
      // Create meta tag if it doesn't exist (shouldn't happen, but fallback)
      const metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'theme-color');
      metaTag.setAttribute('content', themeColor);
      document.head.appendChild(metaTag);

      if (process.env.NODE_ENV === 'development') {
        debugLog('[CollectionsPage][themeColor] ⚠️ Created new meta theme-color tag with value:', themeColor);
      }
    }
  }, [themeColor]);

  // Center splash scroll on mount
  useEffect(() => {
    if (splashScrollContainerRef.current) {
      const container = splashScrollContainerRef.current;
      requestAnimationFrame(() => {
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        if (scrollWidth > clientWidth) {
          const centerPosition = (scrollWidth - clientWidth) / 2;
          container.scrollLeft = centerPosition;
        }
      });
    }
  }, []);

  // Safari-specific SVG scaling fix for splash window
  useEffect(() => {
    const fixSafariSvgScaling = () => {
      debugLog('🔧 Safari fix function called');
      if (!splashWindowContainerRef.current) {
        debugWarn('⚠️ splashWindowContainerRef.current is null in Safari fix');
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const svgElements = splashWindowContainerRef.current.querySelectorAll('.splash-window-svg');

      debugLog('🔧 Found SVG elements:', svgElements.length);

      // Original viewBox dimensions
      const originalViewBoxWidth = 376;
      const originalViewBoxHeight = 722;

      // Calculate scaling factors
      const scaleX = viewportWidth / originalViewBoxWidth;
      const scaleY = viewportHeight / originalViewBoxHeight;

      // Force Safari to recalculate SVG dimensions by setting explicit pixel values
      svgElements.forEach((svg, index) => {
        const styles = window.getComputedStyle(svg);
        debugLog(`🔧 SVG ${index} display:`, styles.display);

        if (styles.display !== 'none') {
          // Update SVG dimensions
          svg.setAttribute('width', `${viewportWidth}`);
          svg.setAttribute('height', `${viewportHeight}`);
          svg.setAttribute('viewBox', `0 0 ${viewportWidth} ${viewportHeight}`);
          svg.style.width = `${viewportWidth}px`;
          svg.style.height = `${viewportHeight}px`;

          // Update foreignObject to match new viewBox
          const foreignObjects = svg.querySelectorAll('foreignObject');
          debugLog(`🔧 Found ${foreignObjects.length} foreignObjects`);
          foreignObjects.forEach(fo => {
            fo.setAttribute('width', `${viewportWidth}`);
            fo.setAttribute('height', `${viewportHeight}`);
          });

          // Update mask dimensions to match new viewBox
          // Note: We're using data URI masks now, but still update SVG masks for fallback
          const masks = svg.querySelectorAll('mask');
          debugLog(`🔧 Found ${masks.length} masks`);
          masks.forEach((mask, maskIndex) => {
            const maskId = mask.getAttribute('id');
            debugLog(`🔧 Processing mask ${maskIndex}: ${maskId}`);

            // Update maskUnits to ensure proper scaling
            mask.setAttribute('maskUnits', 'userSpaceOnUse');

            const rects = mask.querySelectorAll('rect');
            debugLog(`🔧 Found ${rects.length} rects in mask ${maskId}`);
            rects.forEach((rect, rectIndex) => {
              if (rectIndex === 0) {
                // First rect is the full-size mask
                rect.setAttribute('width', `${viewportWidth}`);
                rect.setAttribute('height', `${viewportHeight}`);
                debugLog(`🔧 Updated full mask rect to ${viewportWidth}x${viewportHeight}`);
              } else {
                // Second rect is the window cutout - use stored original values to prevent double-scaling
                let origX, origY, origWidth, origHeight;

                if (rect.hasAttribute('data-orig-x')) {
                  // Use stored original values
                  origX = parseFloat(rect.getAttribute('data-orig-x'));
                  origY = parseFloat(rect.getAttribute('data-orig-y'));
                  origWidth = parseFloat(rect.getAttribute('data-orig-width'));
                  origHeight = parseFloat(rect.getAttribute('data-orig-height'));
                } else {
                  // First time - store original values
                  origX = parseFloat(rect.getAttribute('x'));
                  origY = parseFloat(rect.getAttribute('y'));
                  origWidth = parseFloat(rect.getAttribute('width'));
                  origHeight = parseFloat(rect.getAttribute('height'));

                  rect.setAttribute('data-orig-x', `${origX}`);
                  rect.setAttribute('data-orig-y', `${origY}`);
                  rect.setAttribute('data-orig-width', `${origWidth}`);
                  rect.setAttribute('data-orig-height', `${origHeight}`);
                }

                // Scale from original and round
                let x = origX * scaleX;
                let y = origY * scaleY;
                let width = origWidth * scaleX;
                let height = origHeight * scaleY;

                x = Math.round(x * 100) / 100;
                y = Math.round(y * 100) / 100;
                width = Math.round(width * 100) / 100;
                height = Math.round(height * 100) / 100;

                rect.setAttribute('x', `${x}`);
                rect.setAttribute('y', `${y}`);
                rect.setAttribute('width', `${width}`);
                rect.setAttribute('height', `${height}`);
                debugLog(`🔧 Updated cutout rect from ${origX},${origY} ${origWidth}x${origHeight} to ${x},${y} ${width}x${height}`);
              }
            });

            // Force Safari to recognize mask changes by toggling display
            const maskDisplay = mask.style.display;
            mask.style.display = 'none';
            void mask.offsetHeight;
            mask.style.display = maskDisplay || '';
          });

          // Update border SVG coordinates to match new viewBox
          // Border SVGs are now separate from the main SVG, so we need to update them separately
          const borderSvgs = splashWindowContainerRef.current.querySelectorAll('.splash-border-svg');
          debugLog(`🔧 Found ${borderSvgs.length} border SVGs`);

          // Get border rects from the separate border SVGs
          const borderRectsArray = [];
          borderSvgs.forEach(borderSvg => {
            const rects = borderSvg.querySelectorAll('rect[stroke="white"]');
            borderRectsArray.push(...Array.from(rects));
          });

          debugLog(`🔧 Found ${borderRectsArray.length} border rects in border SVGs`);

          // Also update the border SVG dimensions and viewBox
          borderSvgs.forEach((borderSvg, svgIndex) => {
            const styles = window.getComputedStyle(borderSvg);
            if (styles.display !== 'none') {
              borderSvg.setAttribute('width', `${viewportWidth}`);
              borderSvg.setAttribute('height', `${viewportHeight}`);
              borderSvg.setAttribute('viewBox', `0 0 ${viewportWidth} ${viewportHeight}`);
              borderSvg.style.width = `${viewportWidth}px`;
              borderSvg.style.height = `${viewportHeight}px`;
              debugLog(`🔧 Updated border SVG ${svgIndex} to ${viewportWidth}x${viewportHeight}`);
            }
          });

          borderRectsArray.forEach((rect, rectIndex) => {
            // Get original coordinates from data attributes, or from current attributes if not set
            let origX, origY, origWidth, origHeight, origStrokeWidth;

            if (rect.hasAttribute('data-orig-x')) {
              // Use stored original values to prevent double-scaling
              origX = parseFloat(rect.getAttribute('data-orig-x'));
              origY = parseFloat(rect.getAttribute('data-orig-y'));
              origWidth = parseFloat(rect.getAttribute('data-orig-width'));
              origHeight = parseFloat(rect.getAttribute('data-orig-height'));
              origStrokeWidth = parseFloat(rect.getAttribute('data-orig-stroke-width'));
              debugLog(`🔧 Border rect ${rectIndex}: Using stored original values`);
            } else {
              // First time - store original values
              origX = parseFloat(rect.getAttribute('x'));
              origY = parseFloat(rect.getAttribute('y'));
              origWidth = parseFloat(rect.getAttribute('width'));
              origHeight = parseFloat(rect.getAttribute('height'));
              origStrokeWidth = parseFloat(rect.getAttribute('stroke-width'));

              rect.setAttribute('data-orig-x', `${origX}`);
              rect.setAttribute('data-orig-y', `${origY}`);
              rect.setAttribute('data-orig-width', `${origWidth}`);
              rect.setAttribute('data-orig-height', `${origHeight}`);
              rect.setAttribute('data-orig-stroke-width', `${origStrokeWidth}`);
              debugLog(`🔧 Border rect ${rectIndex}: Stored original values`, {
                x: origX, y: origY, width: origWidth, height: origHeight, strokeWidth: origStrokeWidth
              });
            }

            // Scale from original coordinates and round to avoid sub-pixel issues
            let x = origX * scaleX;
            let y = origY * scaleY;
            let width = origWidth * scaleX;
            let height = origHeight * scaleY;
            const strokeWidth = origStrokeWidth * Math.min(scaleX, scaleY);

            // Round to 2 decimal places to match mask precision
            x = Math.round(x * 100) / 100;
            y = Math.round(y * 100) / 100;
            width = Math.round(width * 100) / 100;
            height = Math.round(height * 100) / 100;

            rect.setAttribute('x', `${x}`);
            rect.setAttribute('y', `${y}`);
            rect.setAttribute('width', `${width}`);
            rect.setAttribute('height', `${height}`);
            rect.setAttribute('stroke-width', `${strokeWidth}`);

            // Ensure border renders on top
            rect.setAttribute('style', 'z-index: 2;');

            debugLog(`🔧 Border rect ${rectIndex} scaled:`, {
              from: { x: origX, y: origY, width: origWidth, height: origHeight },
              to: { x, y, width, height, strokeWidth },
              scaleX,
              scaleY,
              rounded: true
            });
          });

          // Apply data URI mask with mask-composite (Safari-compatible approach)
          // Use the SAME coordinates as the border rect to ensure perfect alignment
          const blurDivs = svg.querySelectorAll('.splash-blur-effect');

          blurDivs.forEach((div, divIndex) => {
            const maskType = div.getAttribute('data-mask-type');
            const isMobile = maskType === 'mobile';

            // Get the corresponding border rect to use EXACT same coordinates
            const correspondingBorderRect = borderRectsArray.find(rect => {
              // Match by checking if it's in the right border SVG (mobile vs desktop)
              const svg = rect.closest('svg');
              return isMobile
                ? svg?.classList.contains('splash-border-svg-mobile')
                : svg?.classList.contains('splash-border-svg-desktop');
            });

            let windowX, windowY, windowWidth, windowHeight;

            if (correspondingBorderRect) {
              // Use EXACT same coordinates as border rect (already scaled)
              windowX = parseFloat(correspondingBorderRect.getAttribute('x'));
              windowY = parseFloat(correspondingBorderRect.getAttribute('y'));
              windowWidth = parseFloat(correspondingBorderRect.getAttribute('width'));
              windowHeight = parseFloat(correspondingBorderRect.getAttribute('height'));

              debugLog(`🔧 Using border rect coordinates for mask ${divIndex}:`, {
                x: windowX, y: windowY, width: windowWidth, height: windowHeight
              });
            } else {
              // Fallback: calculate from original (shouldn't happen)
              const origX = isMobile ? 75.2 : 75.2;
              const origY = isMobile ? 129 : 170;
              windowX = origX * scaleX;
              windowY = origY * scaleY;
              windowWidth = 225.6 * scaleX;
              windowHeight = 320 * scaleY;
              debugWarn(`⚠️ Border rect not found for ${maskType}, using calculated values`);
            }

            // Round to avoid sub-pixel rendering issues
            windowX = Math.round(windowX * 100) / 100;
            windowY = Math.round(windowY * 100) / 100;
            windowWidth = Math.round(windowWidth * 100) / 100;
            windowHeight = Math.round(windowHeight * 100) / 100;

            // Create data URI SVG mask with exact same coordinates
            const svgMaskData = encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewportWidth} ${viewportHeight}" preserveAspectRatio="none">
                <rect x="${windowX}" y="${windowY}" width="${windowWidth}" height="${windowHeight}" fill="black"/>
              </svg>
            `).replace(/\s+/g, ' ').trim();

            const maskUrl = `url('data:image/svg+xml;utf8,${svgMaskData}') 0/100% 100%`;
            const linearGradient = 'linear-gradient(#fff, #fff)';

            // Apply mask with composite (exclude the window area)
            div.style.mask = `${maskUrl}, ${linearGradient}`;
            div.style.WebkitMask = `${maskUrl}, ${linearGradient}`;
            div.style.maskComposite = 'exclude';
            div.style.WebkitMaskComposite = 'destination-out';
            div.style.maskSize = '100% 100%';
            div.style.WebkitMaskSize = '100% 100%';

            // Ensure proper z-index and rendering layer
            div.style.zIndex = '1';
            div.style.isolation = 'isolate';

            void div.offsetHeight; // Force reflow

            debugLog(`🔧 Applied data URI mask to div ${divIndex} (${maskType}):`, {
              windowCoords: { x: windowX, y: windowY, width: windowWidth, height: windowHeight },
              viewportSize: { width: viewportWidth, height: viewportHeight },
              borderRectCoords: correspondingBorderRect ? {
                x: correspondingBorderRect.getAttribute('x'),
                y: correspondingBorderRect.getAttribute('y'),
                width: correspondingBorderRect.getAttribute('width'),
                height: correspondingBorderRect.getAttribute('height')
              } : null
            });
          });

          // Trigger reflow
          void svg.offsetHeight;

          debugLog('🔧 Safari fix applied:', {
            svgWidth: svg.getAttribute('width'),
            svgHeight: svg.getAttribute('height'),
            viewBox: svg.getAttribute('viewBox'),
            scaleX,
            scaleY,
            computedWidth: window.getComputedStyle(svg).width,
            computedHeight: window.getComputedStyle(svg).height,
            viewportWidth,
            viewportHeight
          });
        } else {
          debugLog(`🔧 SVG ${index} is hidden, skipping`);
        }
      });
    };

    // Fix on mount and resize with multiple attempts
    const timeoutId1 = setTimeout(fixSafariSvgScaling, 0);
    const timeoutId2 = setTimeout(fixSafariSvgScaling, 100);
    const timeoutId3 = setTimeout(fixSafariSvgScaling, 500);
    window.addEventListener('resize', fixSafariSvgScaling);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      window.removeEventListener('resize', fixSafariSvgScaling);
    };
  }, []);

  // Debug blur mask and border alignment
  useEffect(() => {
    if (!isDebugLoggingEnabled()) {
      return undefined;
    }

    const logDebugInfo = () => {
      debugLog('🔍 [CollectionsPage] Debug function called, checking ref...');
      if (!splashWindowContainerRef.current) {
        debugWarn('⚠️ splashWindowContainerRef.current is null');
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth < 769;
      const activeVersion = isMobile ? 'mobile' : 'desktop';

      // SVG viewBox dimensions
      const viewBoxWidth = 376;
      const viewBoxHeight = 722;

      // Window coordinates in SVG space
      const windowX = 75.2;
      const windowWidth = 225.6;
      const windowHeight = 320;
      const windowY = isMobile ? 129 : 170;

      // Calculate scaling factors
      const scaleX = viewportWidth / viewBoxWidth;
      const scaleY = viewportHeight / viewBoxHeight;

      // Expected rendered dimensions in pixels
      const expectedWindowX = windowX * scaleX;
      const expectedWindowY = windowY * scaleY;
      const expectedWindowWidth = windowWidth * scaleX;
      const expectedWindowHeight = windowHeight * scaleY;

      // Get actual SVG element dimensions
      const svgElements = splashWindowContainerRef.current.querySelectorAll('.splash-window-svg');
      const activeSvg = Array.from(svgElements).find(svg => {
        const styles = window.getComputedStyle(svg);
        return styles.display !== 'none';
      });

      let actualSvgRect = null;
      if (activeSvg) {
        actualSvgRect = activeSvg.getBoundingClientRect();
      }

      // Get container dimensions
      const containerRect = splashWindowContainerRef.current.getBoundingClientRect();

      console.group('🔍 SplashPage Blur Mask Debug');
      debugLog('Viewport:', { width: viewportWidth, height: viewportHeight });
      debugLog('Active version:', activeVersion);
      debugLog('SVG viewBox:', { width: viewBoxWidth, height: viewBoxHeight });
      debugLog('Window coordinates (SVG space):', {
        x: windowX,
        y: windowY,
        width: windowWidth,
        height: windowHeight
      });
      debugLog('Scaling factors:', { scaleX, scaleY, aspectRatio: scaleX / scaleY });
      debugLog('Expected window (pixels):', {
        x: expectedWindowX,
        y: expectedWindowY,
        width: expectedWindowWidth,
        height: expectedWindowHeight
      });
      debugLog('Container rect:', containerRect);
      if (actualSvgRect) {
        debugLog('Active SVG rect:', actualSvgRect);
        debugLog('SVG scaling vs viewport:', {
          svgWidth: actualSvgRect.width,
          svgHeight: actualSvgRect.height,
          viewportWidth,
          viewportHeight,
          widthRatio: actualSvgRect.width / viewportWidth,
          heightRatio: actualSvgRect.height / viewportHeight
        });
      } else {
        debugWarn('⚠️ No active SVG found');
      }
      console.groupEnd();
    };

    // Log on mount and resize with multiple attempts
    const timeoutId1 = setTimeout(logDebugInfo, 100);
    const timeoutId2 = setTimeout(logDebugInfo, 500);
    window.addEventListener('resize', logDebugInfo);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      window.removeEventListener('resize', logDebugInfo);
    };
  }, []);

  useEffect(() => {
    const updateSplashWindowMetrics = () => {
      const splashContainer = splashWindowContainerRef.current?.closest('.splash-container');
      if (!splashContainer) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth < 769;
      const scaleX = viewportWidth / 376;
      const scaleY = viewportHeight / 722;
      const windowLeft = 75.2 * scaleX;
      const windowTop = (isMobile ? 129 : 170) * scaleY;
      const windowBottom = ((isMobile ? 129 : 170) + 320) * scaleY;
      const rootStyle = document.documentElement.style;

      splashContainer.style.setProperty('--splash-window-left', `${windowLeft}px`);
      splashContainer.style.setProperty('--splash-window-top', `${windowTop}px`);
      splashContainer.style.setProperty('--splash-window-bottom', `${windowBottom}px`);
      rootStyle.setProperty('--splash-window-left', `${windowLeft}px`);
      rootStyle.setProperty('--splash-window-top', `${windowTop}px`);
      rootStyle.setProperty('--splash-window-bottom', `${windowBottom}px`);
    };

    updateSplashWindowMetrics();
    window.addEventListener('resize', updateSplashWindowMetrics);
    window.visualViewport?.addEventListener('resize', updateSplashWindowMetrics);

    return () => {
      window.removeEventListener('resize', updateSplashWindowMetrics);
      window.visualViewport?.removeEventListener('resize', updateSplashWindowMetrics);
      document.documentElement.style.removeProperty('--splash-window-left');
      document.documentElement.style.removeProperty('--splash-window-top');
      document.documentElement.style.removeProperty('--splash-window-bottom');
    };
  }, []);

  // Show loading if projects haven't loaded yet
  if (!projects || projects.length === 0) {
    return <Loading />;
  }

  if (collections.length === 0) {
    return (
      <div className="no-collections">
        <p>No collection images available</p>
      </div>
    );
  }

  const headerProject = getHeaderProject(projects, location.pathname);
  const fallbackHeaderProject = headerProject
    || projects.find((project) => project.status === 'Public' && project.id !== 'about')
    || aboutData
    || null;
  let activeProject = fallbackHeaderProject;
  let activeProjectId = null;
  const shouldPrioritizeCollectionCarousel = (index) => !isMobileViewport
    || currentSectionIndex === index
    || currentSectionIndex === index - 1
    || currentSectionIndex === index + 1
    || (currentSectionIndex === -1 && index === 0)
    || (currentSectionIndex === collections.length && index === collections.length - 1);

  if (currentSectionIndex === -1) {
    activeProjectId = 'home';
  } else if (currentSectionIndex === collections.length) {
    activeProject = aboutData || projects.find((project) => project.id === 'about') || fallbackHeaderProject;
    activeProjectId = 'about';
  } else if (currentSection) {
    activeProject = projects.find((project) => project.id === currentSection.projectId) || fallbackHeaderProject;
    activeProjectId = currentSection.projectId;
  }

  // Debug: Log active project determination
  if (process.env.NODE_ENV === 'development') {
    debugLog('[CollectionsPage] activeProject determination', {
      currentSectionIndex,
      locationPathname: location.pathname,
      headerProjectId: headerProject?.id,
      fallbackHeaderProjectId: fallbackHeaderProject?.id,
      activeProjectId: activeProject?.id,
      activeRailProjectId: activeProjectId,
      currentSectionId: currentSection?.projectId,
      collectionsLength: collections.length,
      isAboutSection: currentSectionIndex === collections.length
    });
  }

  return (
    <>
      <Helmet>
        <meta name="theme-color" content={themeColor} />
      </Helmet>
      {activeProject && (
        <Header
          projects={projects}
          project={activeProject}
          activeProjectId={activeProjectId}
          onProjectSelect={onProjectSelect}
          showBackToTimeline={false}
        />
      )}
      {currentSectionIndex === -1 && !hasEventQuery && (
        <MetaTags {...defaultMetaData} url="https://www.elodiecarstensen.com/" />
      )}
      {currentSectionIndex === collections.length && !hasEventQuery && (
        <MetaTags {...getAboutMetaData()} />
      )}
      {currentSection && <MetaTags {...metaData} />}
      <div className="collection-carousels-container" ref={scrollContainerRef}>
        {/* Splash Section - First section */}
        <section
          id="splash"
          className="collection-section splash-section"
          data-section-index={-1}
        >
          <div className="splash-container">
            <div className="splash-window-container" ref={splashWindowContainerRef}>
              <div className="splash-window-wrapper">
                <svg className="splash-window-svg splash-window-svg-mobile" fill="none" preserveAspectRatio="none" viewBox="0 0 376 722" aria-hidden="true">
                  <defs>
                    <mask id="splash-blur-mask-mobile" maskUnits="userSpaceOnUse">
                      <rect width="376" height="722" fill="white" />
                      <rect x="75.2" y="129" width="225.6" height="320" fill="black" />
                    </mask>
                    <mask id="splash-border-mask-mobile" maskUnits="userSpaceOnUse">
                      <rect width="376" height="722" fill="black" />
                      <rect x="75.2" y="129" width="225.6" height="320" fill="white" />
                    </mask>
                  </defs>
                  <foreignObject x="0" y="0" width="376" height="722">
                    <div
                      className="splash-blur-effect"
                      data-mask-type="mobile"
                      style={{
                        backdropFilter: 'blur(7.5px)',
                        WebkitBackdropFilter: 'blur(7.5px)',
                        background: 'rgba(217, 217, 217, 0.01)',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none'
                      }}
                      xmlns="http://www.w3.org/1999/xhtml"
                    />
                  </foreignObject>
                </svg>
                {/* Border SVG - separate, always visible, unmasked, on top */}
                <svg
                  className="splash-border-svg splash-border-svg-mobile"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 376 722"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                  aria-hidden="true"
                >
                  <rect
                    x="75.2"
                    y="129"
                    width="225.6"
                    height="320"
                    fill="none"
                    stroke="white"
                    strokeWidth="0.5"
                  />
                </svg>
                <svg className="splash-window-svg splash-window-svg-desktop" fill="none" preserveAspectRatio="none" viewBox="0 0 376 722" aria-hidden="true">
                  <defs>
                    <mask id="splash-blur-mask-desktop" maskUnits="userSpaceOnUse">
                      <rect width="376" height="722" fill="white" />
                      <rect x="75.2" y="170" width="225.6" height="320" fill="black" />
                    </mask>
                    <mask id="splash-border-mask-desktop" maskUnits="userSpaceOnUse">
                      <rect width="376" height="722" fill="black" />
                      <rect x="75.2" y="170" width="225.6" height="320" fill="white" />
                    </mask>
                  </defs>
                  <foreignObject x="0" y="0" width="376" height="722">
                    <div
                      className="splash-blur-effect"
                      data-mask-type="desktop"
                      style={{
                        backdropFilter: 'blur(7.5px)',
                        WebkitBackdropFilter: 'blur(7.5px)',
                        background: 'rgba(217, 217, 217, 0.01)',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none'
                      }}
                      xmlns="http://www.w3.org/1999/xhtml"
                    />
                  </foreignObject>
                </svg>
                {/* Border SVG - separate, always visible, unmasked, on top */}
                <svg
                  className="splash-border-svg splash-border-svg-desktop"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 376 722"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                  aria-hidden="true"
                >
                  <rect
                    x="75.2"
                    y="170"
                    width="225.6"
                    height="320"
                    fill="none"
                    stroke="white"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
            </div>

            <div className="splash-content" aria-hidden="true">
              <div className="splash-title-container splash-title-at-bottom" aria-hidden="true">
                <h1 className="splash-title">Elodie Carstensen</h1>
              </div>
              <div className="splash-text-container">
                <p className="splash-intro-text">
                  {aboutData?.intro || "Is a Berlin‑based demi‑couture label and laboratory where neo‑romanticism meets futurism, creating made‑to‑order collections and collaborative, immersive performances."}
                </p>
              </div>
            </div>
            <div
              className="splash-scroll-container"
              ref={splashScrollContainerRef}
              onClick={() => {
                // Scroll to next section (first collection, index 0)
                // If no collections, scroll to about section if available
                if (collections.length > 0) {
                  if (process.env.NODE_ENV === 'development') {
                    debugLog('[CollectionsPage] Splash click: scrolling to first collection (index 0)');
                  }
                  scrollToSection(0, true); // Use smooth scrolling for user interaction
                } else if (aboutData) {
                  if (process.env.NODE_ENV === 'development') {
                    debugLog('[CollectionsPage] Splash click: no collections, scrolling to about section');
                  }
                  scrollToSection(collections.length, true); // Use smooth scrolling for user interaction
                }
              }}
            >
              <div className="splash-background"
                style={{
                  backgroundImage: `url(${isHighResLoaded ? (isSplashWide ? images.splash.wide : images.splash.full) : images.splash.lowRes})`,
                  transition: 'background-image 0.5s ease-in'
                }}
                aria-hidden="true"
              />
            </div>
          </div>
        </section>

        {/* Collection sections */}
        {collections.map((collection, index) => (
          <section
            key={collection.projectId}
            id={collection.projectId}
            className="collection-section"
            data-section-index={index}
            style={{ '--theme-color': collection.themeColor }}
          >
            {/* Hide collection header if we have an info card - it's redundant */}
            {!collection.introText && (
              <div className="collection-header">
                <h1 className="collection-title">{collection.projectName}</h1>
                {collection.season && <span className="collection-season">{collection.season}</span>}
              </div>
            )}

            <div className="collection-carousel-wrapper">
              <CustomCarousel
                products={enrichProductsWithGarmentData(collection, garments)}
                onProductClick={null}
                navigateOnAnySlide={true}
                isPriorityActive={shouldPrioritizeCollectionCarousel(index)}
                collectionInfo={collection.introText ? {
                  projectId: collection.projectId,
                  projectName: collection.projectName,
                  season: collection.season,
                  introText: collection.introText,
                  themeColor: collection.themeColor,
                  aboutImageSrc: collection.aboutImageSrc
                } : null}
              />
            </div>
          </section>
        ))}

        {/* About section (TimelinePage) after all collections */}
        {aboutData && (
          <section
            id="about"
            className="collection-section about-section"
            data-section-index={collections.length}
          >
            <div className="about-section-wrapper">
              <TimelinePage
                data={aboutData}
                isEmbedded={true}
                isAboutSectionActive={currentSectionIndex === collections.length}
              />
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default CollectionsPage;
